import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import Loader from '../../BaseComponents/Loader';
import {
  deleteMemberReport,
  downloadMemberReport,
  MemberReportData,
  updateReportExtractedData
} from '../../../api/memberReport';
import { parseDexaReportByFilename } from '../../../api/corevitals';
import ExtractedDataModal from '../ExtractedDataModal';

interface IProps {
  report: MemberReportData;
  onRefresh: () => void;
}

const DexaReportsTableRow: React.FC<IProps> = ({ report, onRefresh }) => {
  const location = useLocation();
  const [isExtracting, setIsExtracting] = useState(false);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [extractedData, setExtractedData] = useState<Record<string, unknown> | null>(
    report.extracted_data
  );

  useEffect(() => {
    setExtractedData(report.extracted_data);
  }, [report.extracted_data]);

  const hasExtractedData =
    extractedData !== null &&
    typeof extractedData === 'object' &&
    Object.keys(extractedData).length > 0;

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const formatReportType = (reportType: string): string => {
    if (reportType === 'dexa_scan') return 'DEXA Scan';
    return reportType;
  };

  const normalizeExtractedPayload = (data: Record<string, any>): Record<string, unknown> => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data;
    }
    return { raw: data };
  };

  const saveExtractedData = async (data: Record<string, unknown>) => {
    const memberId = location.pathname.split('/')[2];
    if (!memberId) {
      throw new Error('Member ID not found');
    }
    const updated = await updateReportExtractedData(memberId, report.id, data);
    setExtractedData(updated.extracted_data || data);
    return updated;
  };

  const isExtractableFile = (fileName: string, filePath?: string): boolean =>
    /\.(pdf|doc|docx|zip)$/i.test(fileName) || (filePath ? /\.(pdf|doc|docx|zip)$/i.test(filePath) : false);

  const handleExtract = async () => {
    if (!isExtractableFile(report.file_name, report.file_path)) {
      alert(
        'Only PDF, DOC, DOCX, and ZIP files are supported for DEXA extraction. Please re-upload the report in one of those formats.'
      );
      return;
    }

    try {
      setIsExtracting(true);
      const response = await parseDexaReportByFilename(report.file_name, report.file_path);

      if (!response.success || !response.data) {
        alert(response.error || 'Failed to extract data from the report');
        return;
      }

      const payload = normalizeExtractedPayload(response.data);
      await saveExtractedData(payload);
      toast.success('Extraction complete. Data saved.');
      onRefresh();
    } catch (error: any) {
      alert(error?.message || 'Failed to extract report data. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  const openExtractedDataModal = (mode: 'view' | 'edit') => {
    if (!hasExtractedData) {
      toast.info('No extracted data available yet. Run Extract first.');
      return;
    }
    setModalMode(mode);
    setIsExtractionModalOpen(true);
  };

  const onActionClick = async (id: number, action: string) => {
    try {
      const memberId = location.pathname.split('/')[2];
      if (!memberId) return;

      if (action === 'download') {
        const presignedUrl = await downloadMemberReport(memberId, id);
        const a = document.createElement('a');
        a.href = presignedUrl;
        a.download = report.file_name;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else if (action === 'extract') {
        await handleExtract();
      } else if (action === 'view') {
        openExtractedDataModal('view');
      } else if (action === 'update') {
        openExtractedDataModal('edit');
      } else if (action === 'delete') {
        if (!window.confirm('Are you sure you want to delete this report?')) return;
        await deleteMemberReport(memberId, id);
        toast.success('Report deleted');
        onRefresh();
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to perform action. Please try again.');
    }
  };

  const handleSaveExtractedData = async (data: Record<string, unknown>) => {
    await saveExtractedData(data);
    toast.success('Extracted data updated');
    setIsExtractionModalOpen(false);
    onRefresh();
  };

  const actionOptions = [
    { key: 'download', value: 'Download' },
    ...(hasExtractedData
      ? [
          { key: 'view', value: 'View Extracted Data' },
          { key: 'update', value: 'Update Extracted Data' }
        ]
      : [{ key: 'extract', value: isExtracting ? 'Extracting...' : 'Extract' }]),
    { key: 'delete', value: 'Delete' }
  ];

  return (
    <>
      <div className={`table-row dexa-reports-table-row ${isExtracting ? 'extracting' : ''}`}>
        <div className='test-date'>{formatDate(report.report_date || report.upload_date)}</div>
        <div className='test-name'>
          {report.name || report.file_name}
          {hasExtractedData && <span className='extracted-badge'>Extracted</span>}
        </div>
        <div className='test-type'>{formatReportType(report.report_type)}</div>
        <div className='test-status'>
          <span className={`status ${report.status.toLowerCase()}`}>{report.status}</span>
        </div>
        <div className='actions'>
          <TableRowActionMenu
            options={actionOptions}
            id={report.id}
            onActionMenuClick={onActionClick}
          />
        </div>
        {isExtracting && (
          <div className='extraction-loading-overlay'>
            <Loader />
            <span className='extraction-loading-text'>Extracting and saving report data...</span>
          </div>
        )}
      </div>

      <ExtractedDataModal
        isOpen={isExtractionModalOpen}
        onClose={() => setIsExtractionModalOpen(false)}
        extractedData={extractedData}
        mode={modalMode}
        onSave={handleSaveExtractedData}
      />
    </>
  );
};

export default DexaReportsTableRow;
